import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useNodeMapShallowSelector } from '../../../../features/keyOperations/store/NodeMapStore'

/**
 * Hook interface for CKEditor toggle functionality
 */
interface CKEditorToggle {
    activate: () => void
    deactivate: () => void
}

/**
 * Configuration for the save-aware editor activation hook
 */
interface SaveAwareEditorActivationConfig {
    /** CKEditor toggle controls */
    ckeToggle: CKEditorToggle
    /** Function to set click coordinates for cursor positioning */
    setClickCoordinates: (coords: { x: number; y: number } | undefined) => void
}

/**
 * Return type for the save-aware editor activation hook
 */
interface SaveAwareEditorActivationResult {
    /** Click handler with save-aware coordinate preservation */
    handleClickWithSaveSupport: (event: React.MouseEvent<HTMLElement>, originalHandler: (event: React.MouseEvent<HTMLElement>) => void) => void
}

/**
 * Custom hook that handles editor activation during save operations.
 * 
 * **Problem Solved:**
 * When users click on content during save operations, CKEditor can get stuck in a 
 * non-interactive state even after the save completes. This hook ensures that 
 * clicked content automatically reactivates with proper cursor positioning.
 * 
 * **How it works:**
 * 1. Tracks clicks that occur during save operations
 * 2. Preserves click coordinates for cursor positioning
 * 3. Forces editor reactivation when save completes
 * 4. Restores original click coordinates for accurate cursor placement
 * 
 * @param config - Configuration object containing ckeToggle and setClickCoordinates
 * @returns Object with save-aware click handler
 * 
 * @example
 * ```typescript
 * const { handleClickWithSaveSupport } = useSaveAwareEditorActivation({
 *   ckeToggle,
 *   setClickCoordinates
 * })
 * 
 * // Use in onClick handler
 * onClick={(event) => handleClickWithSaveSupport(event, originalClickHandler)}
 * ```
 */
export const useSaveAwareEditorActivation = ({
    ckeToggle,
    setClickCoordinates,
}: SaveAwareEditorActivationConfig): SaveAwareEditorActivationResult => {
    // Track save operation state
    const isSaveOperationInProgress = useNodeMapShallowSelector((state) => state.isSaveOperationInProgress)

    // State tracking refs
    const wasClickedDuringSave = useRef(false)
    const savedClickCoordinates = useRef<{ x: number; y: number } | undefined>(undefined)
    const prevSaveOperationInProgress = useRef(isSaveOperationInProgress)

    /**
     * Effect that handles forced reactivation when save operations complete
     */
    useEffect(() => {
        const wasSaveInProgress = prevSaveOperationInProgress.current
        prevSaveOperationInProgress.current = isSaveOperationInProgress
        
        // Check if save just completed and this content was clicked during save
        if (wasSaveInProgress && !isSaveOperationInProgress && wasClickedDuringSave.current) {
            const savedCoords = savedClickCoordinates.current
            
            wasClickedDuringSave.current = false
            savedClickCoordinates.current = undefined
            
            requestAnimationFrame(() => {
                ckeToggle?.deactivate?.()
                requestAnimationFrame(() => {
                    // Restore original click coordinates for proper cursor positioning
                    if (savedCoords) {
                        setClickCoordinates(savedCoords)
                    }
                    ckeToggle?.activate?.()
                })
            })
        }
    }, [isSaveOperationInProgress, ckeToggle, setClickCoordinates])

    /**
     * Save-aware click handler that preserves coordinates during save operations
     */
    const handleClickWithSaveSupport = useCallback((
        event: React.MouseEvent<HTMLElement>,
        originalHandler: (event: React.MouseEvent<HTMLElement>) => void
    ) => {
        if (isSaveOperationInProgress) {
            wasClickedDuringSave.current = true
            // Preserve click coordinates for cursor positioning
            savedClickCoordinates.current = {
                x: event.clientX,
                y: event.clientY
            }
        }
        
        // Execute the original click handler
        originalHandler(event)
    }, [isSaveOperationInProgress])

    return useMemo(() => ({
        handleClickWithSaveSupport,
    }), [handleClickWithSaveSupport])
}
